package br.com.htms.inspeccaocampo.auth

import br.com.htms.inspeccaocampo.data.AppConfig
import br.com.htms.inspeccaocampo.data.FormAcessoConfig
import br.com.htms.inspeccaocampo.data.FormTipo
import br.com.htms.inspeccaocampo.data.ObraItem
import br.com.htms.inspeccaocampo.data.AcompAmbDossier
import br.com.htms.inspeccaocampo.data.AcompAmbMonthRow
import br.com.htms.inspeccaocampo.data.RemDossier
import br.com.htms.inspeccaocampo.data.RelSegDossier
import br.com.htms.inspeccaocampo.data.RemMonthRow

/** TAGs do usuário (função em campo). Não liberam acesso: só sugerem o padrão em [Acessos.padraoDasTags]. */
object ProfileTypes {
    const val TST = "TST"
    const val ADM = "ADM"

    const val SUPERVISOR = "SUPERVISOR"
    const val ENGENHEIRO = "ENGENHEIRO"

    private val ordem = listOf(TST, ADM, SUPERVISOR, ENGENHEIRO)
    private val idRegex = Regex("^[A-Z][A-Z0-9_]{0,20}$")

    fun normalize(raw: Collection<String>?): List<String> {
        val vistos = linkedSetOf<String>()
        raw.orEmpty().forEach { item ->
            item.split(',', ';', '|', ' ').forEach { parte ->
                val tipo = parte.trim().uppercase()
                if (idRegex.matches(tipo)) vistos.add(tipo)
            }
        }
        return ordem.filter { it in vistos } + vistos.filter { it !in ordem }
    }

    fun rotulo(tipo: String): String = when (tipo) {
        SUPERVISOR -> "Supervisor"
        ENGENHEIRO -> "Engenheiro"
        else -> tipo
    }

    fun fromEncoded(raw: String?): List<String> =
        normalize(
            raw?.split(',', ';', '|', ' ')
                ?.map { it.trim() }
                ?.filter { it.isNotEmpty() }
        )

    fun encode(types: Collection<String>): String = normalize(types).joinToString(",")

    fun label(types: Collection<String>): String {
        val n = normalize(types)
        return if (n.isEmpty()) "—" else n.joinToString(" + ") { rotulo(it) }
    }

    fun liberaForm(acesso: FormAcessoConfig, types: Collection<String>): Boolean =
        (acesso.tst && TST in types) ||
            (acesso.adm && ADM in types) ||
            (acesso.supervisor && SUPERVISOR in types) ||
            (acesso.engenheiro && ENGENHEIRO in types)
}

/**
 * Formulários e módulos que o Master libera por usuário (users.acessos no servidor).
 * As chaves de formulário são os ids de [FormTipo]; o Relatório Alojamento libera também as suas partes.
 */
object Acessos {
    const val ATIVOS = "ATIVOS"
    /** Legado: unificado em [COLABORADORES] (matriz QSMS + emissão de documentos). */
    const val EMISSAO_DOC = "EMISSAO_DOC"
    const val COLABORADORES = "COLABORADORES"
    const val EFETIVO = "EFETIVO"
    const val OCORRENCIAS = "OCORRENCIAS"
    /** Módulo Dashboards (indicadores de todos os módulos liberados). */
    const val DASHBOARDS = "DASHBOARDS"
    const val ESTOQUE = "ESTOQUE"

    const val ATIVOS_EDITAR = "ATIVOS_EDITAR"
    const val ATIVOS_EXCLUIR = "ATIVOS_EXCLUIR"
    const val COLABORADORES_EDITAR = "COLABORADORES_EDITAR"
    const val COLABORADORES_EXCLUIR = "COLABORADORES_EXCLUIR"
    const val EFETIVO_EDITAR = "EFETIVO_EDITAR"
    const val EFETIVO_EXCLUIR = "EFETIVO_EXCLUIR"
    const val OCORRENCIAS_EDITAR = "OCORRENCIAS_EDITAR"
    const val OCORRENCIAS_EXCLUIR = "OCORRENCIAS_EXCLUIR"
    const val ESTOQUE_EDITAR = "ESTOQUE_EDITAR"
    const val ESTOQUE_EXCLUIR = "ESTOQUE_EXCLUIR"

    val formularios: List<FormTipo>
        get() = FormTipo.hubEntries

    val modulos: List<String> = listOf(ATIVOS, COLABORADORES, EFETIVO, OCORRENCIAS, DASHBOARDS, ESTOQUE)

    /** Edição e exclusão do conteúdo. Só vale para usuário comum; admin do módulo não usa. */
    val acoes: List<String> = listOf(
        ATIVOS_EDITAR, ATIVOS_EXCLUIR,
        COLABORADORES_EDITAR, COLABORADORES_EXCLUIR,
        EFETIVO_EDITAR, EFETIVO_EXCLUIR,
        OCORRENCIAS_EDITAR, OCORRENCIAS_EXCLUIR,
        ESTOQUE_EDITAR, ESTOQUE_EXCLUIR
    )

    fun rotuloModulo(chave: String): String = when (chave) {
        ATIVOS -> "Controle de Ativos"
        EMISSAO_DOC, COLABORADORES -> "Colaboradores QSMS (matriz + documentos)"
        EFETIVO -> "Controle de Efetivo"
        OCORRENCIAS -> "Gestão de Ocorrências"
        DASHBOARDS -> "Dashboards"
        ESTOQUE -> "Estoque"
        else -> chave
    }

    fun rotuloAcao(chave: String): String = when (chave) {
        ATIVOS_EDITAR -> "Editar ativos"
        ATIVOS_EXCLUIR -> "Excluir ativos"
        COLABORADORES_EDITAR -> "Editar colaboradores"
        COLABORADORES_EXCLUIR -> "Excluir colaboradores"
        EFETIVO_EDITAR -> "Editar efetivo"
        EFETIVO_EXCLUIR -> "Excluir efetivo"
        OCORRENCIAS_EDITAR -> "Editar ocorrências"
        OCORRENCIAS_EXCLUIR -> "Excluir ocorrências"
        ESTOQUE_EDITAR -> "Editar estoque"
        ESTOQUE_EXCLUIR -> "Excluir estoque"
        else -> chave
    }

    fun rotulo(chave: String): String =
        FormTipo.entries.firstOrNull { it.id == chave }?.rotulo ?: rotuloModulo(chave)

    fun rotuloCurto(chave: String): String = when (chave) {
        ATIVOS -> "Ativos"
        EMISSAO_DOC, COLABORADORES -> "Colab."
        EFETIVO -> "Efetivo"
        OCORRENCIAS -> "Ocorr."
        DASHBOARDS -> "Dash."
        ESTOQUE -> "Estoque"
        ATIVOS_EDITAR -> "Ed. Ativos"
        ATIVOS_EXCLUIR -> "Exc. Ativos"
        COLABORADORES_EDITAR -> "Ed. Colab."
        COLABORADORES_EXCLUIR -> "Exc. Colab."
        EFETIVO_EDITAR -> "Ed. Efetivo"
        EFETIVO_EXCLUIR -> "Exc. Efetivo"
        OCORRENCIAS_EDITAR -> "Ed. Ocorr."
        OCORRENCIAS_EXCLUIR -> "Exc. Ocorr."
        ESTOQUE_EDITAR -> "Ed. Estoque"
        ESTOQUE_EXCLUIR -> "Exc. Estoque"
        else -> FormTipo.entries.firstOrNull { it.id == chave }?.rotuloCurto ?: chave
    }

    /** Ordem de exibição: formulários do hub e depois os módulos. */
    val todas: List<String>
        get() = formularios.map { it.id } + modulos

    fun normalizar(raw: Collection<String>?): List<String> {
        val set = raw.orEmpty().map { it.trim().uppercase() }.toMutableSet()
        if (EMISSAO_DOC in set) {
            set.add(COLABORADORES)
            set.remove(EMISSAO_DOC)
        }
        set.remove("CONFIG")
        return (todas + acoes).filter { it in set }
    }

    private val padraoPorTag: Map<String, List<String>> = mapOf(
        ProfileTypes.TST to listOf(
            FormTipo.INSPECAO_CAMPO.id,
            FormTipo.RELATORIO_ALOJAMENTO.id,
            FormTipo.CHECKLIST_VETORES.id,
            FormTipo.COMUNICACAO_OCORRENCIAS.id,
            FormTipo.REM_SEGURANCA_OBRAS.id,
            FormTipo.RELATORIO_SEGURANCA.id,
            FormTipo.ACOMPANHAMENTO_AMBIENTAL.id,
            FormTipo.EVIDENCIA_TREINAMENTO.id,
            COLABORADORES,
            DASHBOARDS
        ),
        ProfileTypes.ADM to listOf(
            FormTipo.CHECKLIST_DIARIO_HTMS.id,
            FormTipo.CHECKLIST_DIARIO_AXIA.id,
            FormTipo.REM_SEGURANCA_OBRAS.id,
            FormTipo.RELATORIO_SEGURANCA.id,
            FormTipo.ACOMPANHAMENTO_AMBIENTAL.id,
            FormTipo.EVIDENCIA_TREINAMENTO.id,
            ATIVOS,
            COLABORADORES,
            EFETIVO,
            OCORRENCIAS,
            ESTOQUE,
            DASHBOARDS
        ),
        ProfileTypes.SUPERVISOR to listOf(ATIVOS, DASHBOARDS),
        ProfileTypes.ENGENHEIRO to listOf(ATIVOS, DASHBOARDS)
    )

    fun padraoDasTags(tags: Collection<String>): List<String> =
        normalizar(ProfileTypes.normalize(tags).ifEmpty { listOf(ProfileTypes.TST) }.flatMap { padraoPorTag[it].orEmpty() })

    /** Chave de acesso que libera o [tipo] (partes do alojamento dependem do relatório). */
    fun chaveDoForm(tipo: FormTipo): String = when (tipo) {
        FormTipo.RELATORIO_FOTO_ALOJAMENTO, FormTipo.CHECKLIST_ALOJAMENTO -> FormTipo.RELATORIO_ALOJAMENTO.id
        else -> tipo.id
    }

    fun liberaForm(acessos: Collection<String>, tipo: FormTipo): Boolean = chaveDoForm(tipo) in acessos
}

/** Usuário autenticado (sessão). */
data class AuthUser(
    val id: String,
    val username: String,
    val role: String,
    val profileTypes: List<String> = listOf(ProfileTypes.TST),
    val createdAt: Long? = null,
    /** Nome completo para preenchimento de formulários. */
    val nome: String? = null,
    /** Acessos liberados pelo Master; null = sessão antiga sem o campo (usa a regra por TAG). */
    val acessos: List<String>? = null
) {
    val isAdmin: Boolean get() = role.equals("admin", ignoreCase = true)

    /** Master do sistema (username fixo `admin`) — define TST/ADM dos demais. */
    val isMaster: Boolean get() = username.equals("admin", ignoreCase = true)

    /** Nome para autofill: nome cadastrado ou, se vazio, o login. */
    val displayName: String
        get() = nome?.trim()?.takeIf { it.isNotEmpty() } ?: username

    /**
     * Tipos efetivos para hub/export.
     * Master → TST+ADM; demais usam o gravado no banco (admin também respeita TST/ADM).
     */
    val effectiveProfileTypes: List<String>
        get() = when {
            isMaster -> listOf(ProfileTypes.TST, ProfileTypes.ADM)
            else -> ProfileTypes.normalize(profileTypes).ifEmpty { listOf(ProfileTypes.TST) }
        }

    fun canAccessForm(tipo: FormTipo, config: AppConfig? = null): Boolean {
        if (isMaster) return true
        acessos?.let { return Acessos.liberaForm(it, tipo) }
        val acesso = config?.acessoDoForm(tipo) ?: FormAcessoConfig.padraoPara(tipo)
        return ProfileTypes.liberaForm(acesso, effectiveProfileTypes)
    }

    fun canExportForm(tipo: FormTipo, config: AppConfig? = null): Boolean =
        canAccessForm(tipo, config)

    fun allowedFormTipos(config: AppConfig? = null): List<FormTipo> =
        FormTipo.entries.filter { canAccessForm(it, config) }

    /**
     * Emissão de documentos (OS, EPI, certificados, APR, cadastro de funcionários):
     * mesmo acesso do módulo Colaboradores QSMS.
     */
    fun podeEmitirDocumentos(config: AppConfig?): Boolean = podeAcessarColaboradores ||
        (acessos == null && isAdmin && config?.emissaoDocLiberada(username) == true)

    /** Controle de Ativos: Master ou usuário com o acesso liberado pelo Master. */
    fun podeAcessarAtivos(config: AppConfig?): Boolean =
        isMaster || (acessos?.contains(Acessos.ATIVOS) ?: (config?.ativosLiberado(username) == true))

    /** Colaboradores QSMS (matriz + emissão de documentos): acesso liberado pelo Master. */
    val podeAcessarColaboradores: Boolean
        get() = isMaster ||
            acessos?.let { Acessos.COLABORADORES in it || Acessos.EMISSAO_DOC in it } == true
    val podeAcessarEfetivo: Boolean
        get() = isMaster || acessos?.contains(Acessos.EFETIVO) == true
    val podeAcessarOcorrencias: Boolean
        get() = isMaster || acessos?.contains(Acessos.OCORRENCIAS) == true
    val podeAcessarEstoque: Boolean
        get() = isMaster || acessos?.contains(Acessos.ESTOQUE) == true

    /** Módulo Dashboards. Sem a lista de acessos na sessão, mantém o que já era visível. */
    val podeAcessarDashboards: Boolean
        get() = isMaster || acessos == null || acessos.contains(Acessos.DASHBOARDS)

    /**
     * Identidade, e-mails, checklists, TAGs e obras.
     * Só o admin master altera.
     */
    val podeEditarCamposMaster: Boolean
        get() = isMaster
}

data class AuthSession(
    val token: String,
    val expiresAt: Long,
    val user: AuthUser
)

data class RemoteInspection(
    val id: String,
    val localId: Long?,
    val createdAt: Long,
    val username: String?,
    val summaryTst: String,
    val summaryLocal: String,
    val summaryEfetivo: String?,
    val summaryObra: String? = null,
    val dataHorarioMs: Long? = null,
    /** Nome amigável do PDF (ex.: Inspecao_23-07-2026_Nome_Obra.pdf). */
    val pdfFileName: String? = null,
    /** Nome amigável do Excel. */
    val excelFileName: String? = null,
    /** Discriminador: INSPECAO_CAMPO | RELATORIO_FOTO_ALOJAMENTO | CHECKLIST_ALOJAMENTO | RELATORIO_ALOJAMENTO | COMUNICACAO_OCORRENCIAS */
    val formType: String? = null,
    val hasPdf: Boolean,
    val hasExcel: Boolean,
    val hasJson: Boolean
) {
    val formTipoRotulo: String
        get() = br.com.htms.inspeccaocampo.data.FormTipo.fromId(formType).rotulo
}

/** Linha retornada por [AuthApi.fetchConsolidadoPayloads]. */
data class ConsolidadoPayloadRow(
    val id: String,
    val username: String,
    val json: String
)

data class AuthUserListItem(
    val id: String,
    val username: String,
    val role: String,
    val profileTypes: List<String> = listOf(ProfileTypes.TST),
    val createdAt: Long?,
    val nome: String? = null,
    val acessos: List<String>? = null,
    /** null = todas as abas com edição. Formato `PAINEL,ATIVOS+`. */
    val ativosAbas: String? = null,
    /** null = todos os centros. Lista de ids separada por vírgula quando há limite. */
    val ativosCentros: String? = null
) {
    val displayName: String
        get() = nome?.trim()?.takeIf { it.isNotEmpty() } ?: username
}

/** Entrada leve para autocomplete de nomes nos formulários. */
data class UserDisplayName(
    val username: String,
    val nome: String?,
    val display: String,
    val profileTypes: List<String> = listOf(ProfileTypes.TST),
    val acessos: List<String>? = null
) {
    fun canAccessForm(tipo: FormTipo, config: AppConfig? = null): Boolean {
        acessos?.let { return Acessos.liberaForm(it, tipo) }
        val types = ProfileTypes.normalize(profileTypes).ifEmpty { listOf(ProfileTypes.TST) }
        val acesso = config?.acessoDoForm(tipo) ?: FormAcessoConfig.padraoPara(tipo)
        return ProfileTypes.liberaForm(acesso, types)
    }
}

/** Entrada do log de acesso (admins). */
data class AccessLogEntry(
    val id: String,
    val createdAt: Long,
    val actorUserId: String?,
    val actorUsername: String?,
    val action: String,
    val target: String?,
    val detail: String?,
    val ip: String?
)

/** Item unificado da lixeira (7 dias). */
data class TrashItem(
    val type: String,
    val id: String,
    val label: String,
    val subtitle: String? = null,
    val username: String? = null,
    val obra: String? = null,
    val ano: Int? = null,
    val mes: Int? = null,
    val deletedAt: Long,
    val purgeAt: Long? = null,
    val deletedByUsername: String? = null,
    val formType: String? = null
)

/**
 * API remota de autenticação / histórico / admin.
 * Web/Android/iOS: Worker Cloudflare (NSURLSession no nativo iOS).
 */
expect object AuthApi {
    /** true se a plataforma exige login (API base configurada). */
    fun isLoginRequired(): Boolean

    fun currentSession(): AuthSession?

    suspend fun restoreSession(): AuthSession?

    /** Relê o usuário no servidor (módulos liberados na configuração) sem pedir login de novo. */
    suspend fun refreshSession(): AuthSession?

    suspend fun login(username: String, password: String): Result<AuthSession>

    fun logout()

    suspend fun listInspections(): Result<List<RemoteInspection>>

    /**
     * Admin: lista inspeções com dados para Excel consolidado (sem corte de 60 dias).
     * PDF/Excel podem ter expirado; hasJson permanece se payload estiver no servidor.
     */
    suspend fun listInspectionsForConsolidado(
        fromMs: Long? = null,
        toMs: Long? = null
    ): Result<List<RemoteInspection>>

    /**
     * Admin: baixa payloads JSON em lote (até 100 ids) para o Excel consolidado.
     * Evita um GET por inspeção.
     */
    suspend fun fetchConsolidadoPayloads(ids: List<String>): Result<List<ConsolidadoPayloadRow>>

    /**
     * Envia PDF + Excel (+ JSON opcional) após salvar localmente.
     * @return id remoto ou erro
     */
    suspend fun uploadInspection(
        localId: Long,
        createdAt: Long,
        summaryTst: String,
        summaryLocal: String,
        summaryEfetivo: String?,
        summaryObra: String?,
        dataHorarioMs: Long? = null,
        formType: String? = null,
        emailTo: String? = null,
        pdfFileName: String? = null,
        excelFileName: String? = null,
        pdfBytes: ByteArray?,
        excelBytes: ByteArray?,
        jsonText: String?
    ): Result<String>

    /**
     * Admin: anexa PDF/Excel na inspeção remota existente (não troca o usuário dono).
     * Remove clones criados por regeneração antiga.
     */
    suspend fun attachInspectionAttachments(
        remoteId: String,
        pdfBytes: ByteArray?,
        excelBytes: ByteArray?,
        pdfFileName: String? = null,
        excelFileName: String? = null,
        jsonText: String? = null
    ): Result<Unit>

    suspend fun downloadInspectionBytes(remoteId: String, kind: String): Result<ByteArray>

    /** Admin: remove inspeção remota (D1 + R2). */
    suspend fun deleteInspection(remoteId: String): Result<Unit>

    suspend fun listUsers(): Result<List<AuthUserListItem>>

    /** Nomes para autocomplete (qualquer autenticado). */
    suspend fun listUserDisplayNames(): Result<List<UserDisplayName>>

    suspend fun createUser(
        username: String,
        password: String,
        role: String,
        profileTypes: List<String>,
        nome: String? = null,
        acessos: List<String>? = null
    ): Result<AuthUserListItem>

    suspend fun updateUser(
        userId: String,
        role: String? = null,
        profileTypes: List<String>? = null,
        password: String? = null,
        nome: String? = null,
        acessos: List<String>? = null,
        /** `*` remove a restrição. null não altera o que está gravado. */
        ativosAbas: String? = null,
        ativosCentros: String? = null
    ): Result<AuthUserListItem>

    suspend fun deleteUser(userId: String): Result<Unit>

    /** Admin: últimos eventos de login / gestão de usuários (filtros opcionais no Worker). */
    suspend fun listAccessLogs(
        limit: Int = 2000,
        action: String? = null,
        user: String? = null,
        q: String? = null,
        fromMs: Long? = null,
        toMs: Long? = null
    ): Result<List<AccessLogEntry>>

    /** Admin: itens na lixeira (soft-delete, 7 dias). */
    suspend fun listTrash(): Result<List<TrashItem>>

    /** Admin: recupera item da lixeira. */
    suspend fun restoreTrash(type: String, id: String): Result<Unit>

    /** Admin: apaga definitivo (D1 + R2 inspeções). */
    suspend fun purgeTrash(type: String, id: String): Result<Unit>

    /**
     * Registra evento de auditoria (abertura de formulário, salvar, enviar).
     * Falha silenciosa no cliente — não deve bloquear o fluxo.
     */
    suspend fun logAccessEvent(
        action: String,
        target: String? = null,
        detail: String? = null
    ): Result<Unit>

    /** Obras ativas para o dropdown do formulário (API ou falha → Result.failure). */
    suspend fun listObrasAtivas(): Result<List<ObraItem>>

    /** Todas as obras (admin). */
    suspend fun listObrasAdmin(): Result<List<ObraItem>>

    suspend fun createObra(nome: String): Result<ObraItem>

    suspend fun updateObra(id: String, nome: String? = null, ativo: Boolean? = null): Result<ObraItem>

    /**
     * Configuração global do portal (D1).
     * @return JSON da config, ou null se ainda não houver no servidor.
     */
    suspend fun fetchAppConfigJson(): Result<String?>

    /** Persiste configuração global (somente Master). */
    suspend fun pushAppConfigJson(configJson: String): Result<Unit>

    /** REM — listar dossiês do usuário (admin: all=true lista todos). */
    suspend fun listRemDossiers(all: Boolean = false): Result<List<RemDossier>>

    suspend fun createRemDossier(obra: String, ano: Int): Result<RemDossier>

    suspend fun getRemDossier(id: String): Result<RemDossier>

    suspend fun upsertRemMonth(
        dossierId: String,
        abrangencia: String,
        mes: Int,
        payloadJson: String,
        emailTo: String? = null
    ): Result<RemMonthRow>

    suspend fun deleteRemDossier(id: String): Result<Unit>

    suspend fun downloadRemExcel(dossierId: String): Result<ByteArray>

    /**
     * Zera dados REM no servidor (somente Master).
     * @param scope `mes` | `obra_ano` | `geral`
     */
    suspend fun resetRemAdmin(
        scope: String,
        obra: String? = null,
        ano: Int? = null,
        mes: Int? = null
    ): Result<String>

    suspend fun fetchRemMetasJson(): Result<String>

    suspend fun saveRemMetasJson(body: String): Result<Unit>

    /** Acompanhamento Ambiental — listar dossiês (admin: all=true). */
    suspend fun listAcompAmbDossiers(all: Boolean = false): Result<List<AcompAmbDossier>>

    suspend fun createAcompAmbDossier(obra: String, ano: Int): Result<AcompAmbDossier>

    suspend fun getAcompAmbDossier(id: String): Result<AcompAmbDossier>

    suspend fun upsertAcompAmbMonth(
        dossierId: String,
        aba: String,
        mes: Int,
        payloadJson: String,
        emailTo: String? = null
    ): Result<AcompAmbMonthRow>

    suspend fun deleteAcompAmbDossier(id: String): Result<Unit>

    suspend fun downloadAcompAmbExcel(dossierId: String): Result<ByteArray>

    suspend fun uploadAcompAmbPhoto(
        dossierId: String,
        bytes: ByteArray,
        slot: String,
        index: Int
    ): Result<RelSegPhotoUpload>

    /** Relatorio de Seguranca — listar dossies (admin: all=true). */
    suspend fun listRelSegDossiers(all: Boolean = false): Result<List<RelSegDossier>>

    suspend fun createRelSegDossier(obra: String, ano: Int, mes: Int): Result<RelSegDossier>

    suspend fun getRelSegDossier(id: String): Result<RelSegDossier>

    suspend fun saveRelSegDossier(
        id: String,
        payloadJson: String,
        emailTo: String? = null
    ): Result<RelSegDossier>

    suspend fun deleteRelSegDossier(id: String): Result<Unit>

    suspend fun downloadRelSegPptx(dossierId: String): Result<ByteArray>

    /** Upload foto RelSeg para R2 (prefixo rel-seg/, fora da retenção de 60 dias). */
    suspend fun uploadRelSegPhoto(
        dossierId: String,
        bytes: ByteArray,
        kind: String,
        index: Int? = null,
        block: Int? = null,
        photo: Int? = null
    ): Result<RelSegPhotoUpload>

    /** Assistente visual NR: candidatos do catalogo (Worker AI / fallback); fallback local no cliente. */
    suspend fun analyzeNrVisual(
        cenaTipo: String,
        descricaoCena: String,
        fotoCount: Int,
        fotosBase64: List<String> = emptyList()
    ): Result<br.com.htms.inspeccaocampo.data.nrvisual.NrVisualAnalyzeResult>

    /** Chamada JSON autenticada genérica ([path] começa com `/`). Métodos: GET, POST, PUT, DELETE. */
    suspend fun apiJson(method: String, path: String, body: String? = null): Result<String>

    /** POST autenticado com corpo binário (PDF/imagem); devolve o JSON da resposta. */
    suspend fun apiUploadBytes(path: String, bytes: ByteArray, contentType: String): Result<String>

    /** GET autenticado de arquivo binário. */
    suspend fun apiDownloadBytes(path: String): Result<ByteArray>
}

/** Resultado do upload de foto RelSeg (R2 key + URL GET). */
data class RelSegPhotoUpload(
    val key: String,
    val url: String
)
